import Badge from '@atlaskit/badge';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import FilterIcon from '@atlaskit/icon/core/filter';
import PinIcon from '@atlaskit/icon/core/pin';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import { actionBarPortalState } from '@databases/action-bar/selections-effect';
import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  SendDatabaseSortEventSources,
  sendSortEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import {
  contextualMenuState,
  type MenuData,
} from '@databases/contextual-menu/contextual-menu-state';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { useDialog } from '@databases/dialog';
import { getFieldTypes } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction,
} from '@databases/frontend-metrics/perf-tracing';
import {
  ActiveHorizontalDragHandlerIcon,
  HideIcon,
  HorizontalDragHandlerIcon,
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { useQuickCalcMenuItem } from '@databases/quick-calc/useQuickCalcMenuItem';
import { type HoverProps, useComponent, useKeyboardShortcut } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  doClearEntireFields,
  doDeleteFields,
  doDuplicateEntireFields,
  doInsertFields,
  doMoveFields,
  doSelectNewFields,
} from '@databases/redux/contextual-menu/actions';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseFilterUsageType,
  type DatabaseViewSettings,
} from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import {
  fieldHasCalculations,
  getDatabaseFieldRequired,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';

import { ColSelectButtonContainer, DragHandlerWrapper } from './styles';
import { Position } from './type';

const unfreezeIconContainerStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  height: '16px',
  justifyContent: 'center',
  position: 'relative',
  width: '16px',
});

const unfreezeIconSlashStyles = xcss({
  borderBlockStartColor: 'currentColor',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: token('border.width', '1px'),
  position: 'absolute',
  transform: 'rotate(45deg)',
  width: '18px',
});

const UnfreezeIcon = () => (
  <Box as="span" aria-hidden="true" xcss={unfreezeIconContainerStyles}>
    <PinIcon label="" />
    <Box as="span" xcss={unfreezeIconSlashStyles} />
  </Box>
);

enum SELECT_FIELD_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  PIN_THROUGH_COLUMN = 'pin_through_column',
  UNPIN_COLUMN = 'unpin_column',
  ADD_FIELD_LEFT = 'add_field_left',
  ADD_FIELD_RIGHT = 'add_field_right',
  DUPLICATE_FIELDS = 'duplicate_fields',
  CLEAR_CELLS = 'clear_cells',
  DELETE_FIELDS = 'delete_fields',
  MOVE_FIELDS_LEFT = 'move_fields_left',
  MOVE_FIELDS_RIGHT = 'move_fields_right',
  FILTER_FIELD = 'filter_field',
  SORT_FIELD_INCREASING = 'sort_field_increasing',
  SORT_FIELD_DECREASING = 'sort_field_decreasing',
  HIDE_FIELDS = 'hide_fields',
}

export type PinnedLeadingFieldMenuConfig = {
  fieldIndex: number;
  pinnedLeadingFieldCount: number;
  maxPinnedLeadingFieldCount: number;
  onPinThroughThisColumn: () => void;
  onUnpinAllColumns: () => void;
  onFieldsInserted: ({
    insertBefore,
    selectedFieldIds,
  }: {
    insertBefore: boolean;
    selectedFieldIds: ReadonlyArray<string>;
  }) => void;
  onFieldsRemoved: (fieldIds: ReadonlyArray<string>) => void;
  getViewChangesForFieldsHidden: (
    fieldIds: ReadonlyArray<string>
  ) => ReadonlyArray<ViewChangeType>;
};

type SelectFieldMenuProps = {
  contentId: string;
  fieldId: string;
  currentViewId: string;
  viewSettings?: DatabaseViewSettings;
  hoverProps?: HoverProps;
  canEditFields: boolean;
  handleSelectColumn: (e: React.MouseEvent | React.KeyboardEvent) => void;
  pinnedLeadingMenu?: PinnedLeadingFieldMenuConfig;
};

const getIsFieldSelected = (fieldId: string) =>
  getService('SelectionsReader').isFieldSelected(fieldId);

const SelectFieldMenu: FunctionComponent<SelectFieldMenuProps> = ({
  contentId,
  fieldId,
  currentViewId,
  viewSettings,
  hoverProps,
  handleSelectColumn,
  canEditFields,
  pinnedLeadingMenu,
}) => {
  const selectionsReader = getService('SelectionsReader');
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );
  const actionBar = useSnapshot(actionBarState.state);
  const actionBarPortal = useSnapshot(actionBarPortalState);
  const isActionBarEnabled = isSelectionsPerformanceEnabled
    ? actionBarPortal.isVisible
    : actionBar.isActionBarEnabled;

  // This menu only opens for a single selected field, so isSelected is always
  // true and selectedFieldsCount is always 1 when the menu is visible.
  // Guard the heavier reads behind isSelected so unselected columns skip them.
  const isSelected = selectionsReader.isFieldSelected(fieldId);
  const isDeletionDisabled = isSelected && selectionsReader.isDeleteColumnsDisabled();
  const hasFieldBeforeSelection =
    isSelected && selectionsReader.hasFieldBeforeSelection();
  const hasFieldAfterSelection = isSelected && selectionsReader.hasFieldAfterSelection();
  const canInsertFields = canEditFields && isSelected;
  const canDuplicateFields = canEditFields && isSelected;
  const canDeleteFields = canEditFields && isSelected && !isDeletionDisabled;
  const canClearCells = canEditFields && isSelected;
  const canMoveRight = canEditFields && hasFieldAfterSelection;
  const canMoveLeft = canEditFields && hasFieldBeforeSelection;
  const selectionIntent = isSelected ? selectionsReader.getSelectionIntent() : null;
  const isHoverDeleteFieldsButton =
    selectionIntent?.type === 'delete' && selectionIntent?.target === 'fields';

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { setViewChange, view } = useView();
  const client = useAnalytics();
  const { hidePanel, showPanel } = usePanel();
  const { getData } = usePanelState();
  const { fieldId: panelFieldId } = getData('FieldSettingsPanel') || {};

  const database = selectDatabaseByContentIdRequired(store.getState(), {
    contentId,
  });
  const field = getDatabaseFieldRequired(database, fieldId);

  const isPinnedLeadingColumnsEnabled = getExperimentValue(
    'databasesPinnedLeadingColumns',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const menuData: MenuData = {
    type: 'field',
    fieldId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu({
    type: 'field',
    fieldId,
  });
  const prevIsSelected = useRef(getIsFieldSelected(fieldId));
  const handleMouseDown = useCallback(
    async (e: React.MouseEvent) => {
      if (!isMenuOpen) {
        await client.sendFieldContextualMenuOpened({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsAnalyticsAttributes(contentId),
        });
      }
      toggleOpenMenu();
      if (!getIsFieldSelected(fieldId)) {
        handleSelectColumn(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [client, contentId, fieldId, handleSelectColumn, isMenuOpen, toggleOpenMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (getIsFieldSelected(fieldId) && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectColumn(e);
      }
    },
    [fieldId, handleSelectColumn, isMenuOpen]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();

      if (!isMenuOpen) {
        await client.sendFieldContextualMenuOpened({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsAnalyticsAttributes(contentId),
        });
      }

      toggleOpenMenu();

      if (!getIsFieldSelected(fieldId)) {
        handleSelectColumn(e);
      }
    },
    [client, contentId, fieldId, handleSelectColumn, isMenuOpen, toggleOpenMenu]
  );

  const Trigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <ColSelectButtonContainer className={IGNORED_TABLE_ELEMENT_CLASS} {...hoverProps}>
        <DragHandlerWrapper
          ref={ref}
          type="button"
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && getIsFieldSelected(fieldId)}
          isDeletionActive={isHoverDeleteFieldsButton}
          data-interactive
          data-testid="field-select-button"
          {...triggerProps}
          aria-label={formatMessage(messages['table-layout.select-column-label'])}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onContextMenu={handleRightClick}
          onKeyDown={handleKeyDown}
          style={{
            backgroundColor:
              isMenuOpen && getIsFieldSelected(fieldId)
                ? token('color.background.accent.blue.subtler.pressed')
                : token('color.background.accent.gray.subtlest.hovered'),
          }}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveHorizontalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <HorizontalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </ColSelectButtonContainer>
    ),
    [
      formatMessage,
      handleKeyDown,
      handleMouseDown,
      handleMouseUp,
      hoverProps,
      isMenuOpen,
      fieldId,
      isHoverDeleteFieldsButton,
    ]
  );

  const handleInsertFields = useCallback(
    async (position: Position) => {
      const selectedFieldIds = selectionsReader.getFieldIdsFromSelection();
      const newFieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldTypes()[DatabaseFieldType.TEXT].label),
        }
      );
      await dispatch(
        doSelectNewFields(contentId, () =>
          traceDatabaseAction(
            {
              action:
                position === Position.BEFORE
                  ? DatabaseTraceAction.FieldInsertBefore
                  : DatabaseTraceAction.FieldInsertAfter,
              source: DatabaseTraceSource.FieldMenu,
            },
            () => {
              dispatch(
                doInsertFields(
                  contentId,
                  currentViewId,
                  position === Position.BEFORE,
                  newFieldLabel
                )
              );
              pinnedLeadingMenu?.onFieldsInserted({
                insertBefore: position === Position.BEFORE,
                selectedFieldIds,
              });
            }
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [
      client,
      contentId,
      currentViewId,
      dispatch,
      formatMessage,
      pinnedLeadingMenu,
      selectionsReader,
    ]
  );

  const handleDuplicateFields = useCallback(() => {
    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldDuplicate,
        source: DatabaseTraceSource.FieldMenu,
      },
      () => {
        dispatch(
          doSelectNewFields(contentId, () =>
            dispatch(doDuplicateEntireFields(contentId, view.id))
          )
        );
      }
    );
  }, [dispatch, contentId, view.id]);

  const handleClearFields = useCallback(() => {
    traceDatabaseAction(
      { action: DatabaseTraceAction.FieldClear, source: DatabaseTraceSource.FieldMenu },
      () => {
        dispatch(doClearEntireFields(contentId));
      }
    );
  }, [contentId, dispatch]);

  const { showDialog } = useDialog();

  const onLeaveDeleteFieldButton = useCallback(() => {
    selectionsActions.clearSelectionIntent();
  }, [selectionsActions]);

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const selectedEntireFieldIds = getService('SelectionsReader').getSelectedFieldIds();
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ],
              {
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }
            )}
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteFieldButton();
        if (result) {
          if (getService('SelectionsReader').isFieldInSelection(panelFieldId)) {
            hidePanel();
          }

          if (fieldHasCalculations(fieldId, viewSettings?.calculations)) {
            client.sendRemoveCalculationEvent({
              databaseContainer: getDatabaseContainerType(),
              fieldId,
              fieldType: database.fieldsById[fieldId].type,
              source: 'selectFieldMenu',
              contentId: database.contentId,
            });
          }

          traceDatabaseAction(
            {
              action: DatabaseTraceAction.FieldDelete,
              source: DatabaseTraceSource.FieldMenu,
            },
            () => {
              dispatch(doDeleteFields(contentId));
              pinnedLeadingMenu?.onFieldsRemoved(selectedEntireFieldIds);

              if (isSelectionsPerformanceEnabled) {
                selectionsActions.deselectAll();
              }
            }
          );

          void client.sendDeleteFieldEvent({
            contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        }
      },
    });
  }, [
    store,
    contentId,
    showDialog,
    formatMessage,
    onLeaveDeleteFieldButton,
    panelFieldId,
    fieldId,
    viewSettings?.calculations,
    dispatch,
    client,
    hidePanel,
    isSelectionsPerformanceEnabled,
    pinnedLeadingMenu,
    selectionsActions,
  ]);

  const handleMoveFields = useCallback(
    (position: Position) => {
      traceDatabaseAction(
        {
          action:
            position === Position.BEFORE
              ? DatabaseTraceAction.FieldMoveLeft
              : DatabaseTraceAction.FieldMoveRight,
          source: DatabaseTraceSource.FieldMenu,
        },
        () => {
          dispatch(doMoveFields(contentId, viewSettings, position === Position.BEFORE));
        }
      );

      if (isSelectionsPerformanceEnabled) {
        afterNextLayout(() => selectionsActions.selectFields([fieldId]));
      }
    },
    [
      contentId,
      dispatch,
      fieldId,
      isSelectionsPerformanceEnabled,
      selectionsActions,
      viewSettings,
    ]
  );

  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sorts'][number]) => {
      const matchingSort = view.settings.sorts.find((s) => s.fieldId === sort.fieldId);
      if (matchingSort) {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
          fieldId: sort.fieldId,
          ascending: sort.ascending,
        });
      } else {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_APPEND,
          sort,
        });
      }
      void sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.SORT_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, field, setViewChange, view.settings.sorts]
  );

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;

  const fieldFilters = useMemo(
    () =>
      filters?.value.filter((filter) => 'fieldId' in filter && filter.fieldId === fieldId)
        .length || 0,
    [filters, fieldId]
  );

  const { setActivePanelSetting } = useViewUiState();
  const isViewContextualMenusEnabled = getExperimentValue(
    'databasesViewContextualMenus',
    'enabled',
    { fireExperimentExposure: false }
  );
  const handleFilterField = useCallback(() => {
    manuallyFireExposureEvent('databasesViewContextualMenus');

    if (isViewContextualMenusEnabled) {
      contextualMenuState.showMenu({
        type: 'view-filter',
        viewId: view.id,
        fieldIdForFilterToCreate: fieldId,
      });
    } else {
      setActivePanelSetting(ViewPanelSettingType.FILTER);
      showPanel('ViewSettingsPanel', {
        canEditViews: canEditFields,
        fieldIdForFilterToCreate: fieldId,
      });
    }
  }, [
    fieldId,
    isViewContextualMenusEnabled,
    setActivePanelSetting,
    showPanel,
    canEditFields,
    view.id,
  ]);

  const handleHideFields = useCallback(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const fields = getDatabaseFields(database);
    const field = getDatabaseFieldRequired(database, fieldId);
    const viewChange: ViewChangeType = {
      type: ViewChangeTypes.VIEW_FIELDS,
      fieldKey: fieldId,
      fieldValue: false,
      fields,
    };
    const pinnedLeadingViewChanges =
      pinnedLeadingMenu?.getViewChangesForFieldsHidden([fieldId]) ?? [];
    setViewChange(
      pinnedLeadingViewChanges.length > 0
        ? [viewChange, ...pinnedLeadingViewChanges]
        : viewChange
    );
    if (isSelectionsPerformanceEnabled) {
      selectionsActions.deselectAll();
    }
    void client.sendHideColumnsEvent({
      source: 'databaseView',
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      hidden: true,
      fieldType: field.type,
    });
  }, [
    client,
    contentId,
    fieldId,
    isSelectionsPerformanceEnabled,
    pinnedLeadingMenu,
    selectionsActions,
    setViewChange,
    store,
  ]);

  const addFieldLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT,
      content: formatMessage(messages['field-actions.add-left']),
      onClick: () => handleInsertFields(Position.BEFORE),
      shortcut: SHORTCUTS.ADD_FIELD_LEFT,
      elemBefore: <InsertColumnBeforeIcon label="" />,
    }),
    [formatMessage, handleInsertFields]
  );

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: formatMessage(messages['field-actions.add-right']),
      onClick: () => handleInsertFields(Position.AFTER),
      shortcut: SHORTCUTS.ADD_FIELD_RIGHT,
      elemBefore: <InsertColumnAfterIcon label="" />,
    }),
    [formatMessage, handleInsertFields]
  );

  const duplicateFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS,
      content: formatMessage(messages['field-actions.duplicate'], {
        fieldCount: 1,
      }),
      onClick: handleDuplicateFields,
      shortcut: SHORTCUTS.DUPLICATE,
      elemBefore: <CopyIcon label="" />,
    }),
    [formatMessage, handleDuplicateFields]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearFields,
      shortcut: SHORTCUTS.CLEAR,
      elemBefore: <CrossCircleIcon label="" />,
    }),
    [formatMessage, handleClearFields]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    selectionsActions.setSelectionIntent({ type: 'delete', target: 'fields' });
  }, [selectionsActions]);

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS,
      content: formatMessage(messages['field-actions.delete'], {
        fieldCount: 1,
      }),
      onClick: handleDeleteFields,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      shortcut: SHORTCUTS.DELETE,
      elemBefore: (
        <DeleteIcon
          label=""
          color={
            isHoverDeleteFieldsButton ? token('color.icon.danger', '#C9372C') : undefined
          }
        />
      ),
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
    ]
  );

  const moveFieldsLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT,
      content: formatMessage(messages['field-actions.move-left'], {
        fieldCount: 1,
      }),
      onClick: () => handleMoveFields(Position.BEFORE),
      shortcut: SHORTCUTS.MOVE_FIELDS_LEFT,
      elemBefore: <ArrowLeftIcon label="" />,
    }),
    [formatMessage, handleMoveFields]
  );

  const moveFieldsRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT,
      content: formatMessage(messages['field-actions.move-right'], {
        fieldCount: 1,
      }),
      onClick: () => handleMoveFields(Position.AFTER),
      shortcut: SHORTCUTS.MOVE_FIELDS_RIGHT,
      elemBefore: <ArrowRightIcon label="" />,
    }),
    [formatMessage, handleMoveFields]
  );

  const filterFieldItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD,
      content: formatMessage(messages['field-actions.filter']),
      onClick: handleFilterField,
      elemAfter:
        fieldFilters > 0 ? (
          <Badge
            style={{
              backgroundColor: token(
                'color.background.accent.blue.subtlest.hovered',
                '#CCE0FF'
              ),
            }}
            testId="filters-badge"
          >
            {fieldFilters}
          </Badge>
        ) : undefined,
      elemBefore: <FilterIcon label="" />,
    }),
    [formatMessage, handleFilterField, fieldFilters]
  );

  const sortFieldIncreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING,
      content: formatMessage(messages['field-actions.sort-increasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: true,
        }),
      elemBefore: <SortDescendingIcon label="" />,
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const sortFieldDecreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING,
      content: formatMessage(messages['field-actions.sort-decreasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: false,
        }),
      elemBefore: <SortAscendingIcon label="" />,
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const hideFieldsItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS,
      content: formatMessage(messages['field-actions.hide-fields'], {
        fieldCount: 1,
      }),
      onClick: handleHideFields,
      value: { name: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS },
      elemBefore: <HideIcon label="" />,
    }),
    [formatMessage, handleHideFields]
  );

  const handlePinnedLeadingMenuAction = useCallback(
    ({
      action,
      nextFrozenColumnCount,
      onAction,
    }: {
      action: 'pinned' | 'unpinned';
      nextFrozenColumnCount: number;
      onAction: () => void;
    }) => {
      if (!pinnedLeadingMenu) {
        return;
      }

      const previousFrozenColumnCount = pinnedLeadingMenu.pinnedLeadingFieldCount;

      onAction();
      void client.sendFrozenColumnsUpdatedEvent({
        action,
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'fieldContextualMenu',
        previousFrozenColumnCount,
        nextFrozenColumnCount,
        targetFieldIndex: pinnedLeadingMenu.fieldIndex,
        maxFrozenColumnCount: pinnedLeadingMenu.maxPinnedLeadingFieldCount,
        ...getSelectionsAnalyticsAttributes(contentId),
      });
      selectionsActions.deselectAll();
    },
    [client, contentId, pinnedLeadingMenu, selectionsActions]
  );

  const pinThroughColumnMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    const wouldExceedMax =
      pinnedLeadingMenu.fieldIndex + 1 > pinnedLeadingMenu.maxPinnedLeadingFieldCount;
    const isFieldFrozen =
      pinnedLeadingMenu.fieldIndex < pinnedLeadingMenu.pinnedLeadingFieldCount;

    if (isFieldFrozen) {
      return null;
    }

    return {
      key: SELECT_FIELD_MENU_ACTIONS.PIN_THROUGH_COLUMN,
      content: formatMessage(messages['field-actions.pin-through-column']),
      closeOnClick: !wouldExceedMax,
      isDisabled: wouldExceedMax,
      tooltipDescription: wouldExceedMax
        ? formatMessage(messages['field-actions.pin-through-column-disabled-at-limit'])
        : undefined,
      tooltipPosition: 'right',
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'pinned',
          nextFrozenColumnCount: pinnedLeadingMenu.fieldIndex + 1,
          onAction: pinnedLeadingMenu.onPinThroughThisColumn,
        }),
      elemBefore: <PinIcon label="" />,
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu,
  ]);

  const unpinAllColumnsMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    if (pinnedLeadingMenu.fieldIndex >= pinnedLeadingMenu.pinnedLeadingFieldCount) {
      return null;
    }
    return {
      key: SELECT_FIELD_MENU_ACTIONS.UNPIN_COLUMN,
      content: formatMessage(messages['field-actions.unpin-column']),
      closeOnClick: true,
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'unpinned',
          nextFrozenColumnCount: 0,
          onAction: pinnedLeadingMenu.onUnpinAllColumns,
        }),
      elemBefore: <UnfreezeIcon />,
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu,
  ]);

  const RovoElemBefore = () => {
    useEffect(() => {
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: true,
      });
    }, []);
    return <AskRovoIcon height={16} width={16} noPadding />;
  };

  const askRovoFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ASK_ROVO,
      name: 'ask-rovo',
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () => openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField),
      elemBefore: <RovoElemBefore />,
    }),
    [formatMessage]
  );

  const quickCalculationItem = useQuickCalcMenuItem({
    field,
    contentId,
    canEditFields,
  });

  const menuItems = useMemo(() => {
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      isDBAiFirstCreationEnabled &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false,
      })
    ) {
      firstGroup.push(askRovoFieldMenuItem);
    }
    if (canInsertFields) {
      secondGroup.push(addFieldRightMenuItem);
      secondGroup.push(addFieldLeftMenuItem);
    }
    if (canDuplicateFields) {
      secondGroup.push(duplicateFieldsMenuItem);
    }
    if (canClearCells) {
      secondGroup.push(clearCellsMenuItem);
    }
    if (canDeleteFields) {
      secondGroup.push(deleteFieldsMenuItem);
    }
    if (canMoveLeft) {
      thirdGroup.push(moveFieldsLeftMenuItem);
    }
    if (canMoveRight) {
      thirdGroup.push(moveFieldsRightMenuItem);
    }
    if (unpinAllColumnsMenuItem) {
      fourthGroup.push(unpinAllColumnsMenuItem);
    }
    if (pinThroughColumnMenuItem) {
      fourthGroup.push(pinThroughColumnMenuItem);
    }
    if (quickCalculationItem) {
      fourthGroup.push(quickCalculationItem);
    }
    fourthGroup.push(filterFieldItem);
    fourthGroup.push(sortFieldIncreasingItem);
    fourthGroup.push(sortFieldDecreasingItem);
    fourthGroup.push(hideFieldsItem);
    return [
      { items: firstGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup },
    ];
  }, [
    canInsertFields,
    canDuplicateFields,
    canClearCells,
    canDeleteFields,
    canMoveLeft,
    canMoveRight,
    askRovoFieldMenuItem,
    addFieldRightMenuItem,
    addFieldLeftMenuItem,
    duplicateFieldsMenuItem,
    clearCellsMenuItem,
    deleteFieldsMenuItem,
    moveFieldsLeftMenuItem,
    moveFieldsRightMenuItem,
    quickCalculationItem,
    filterFieldItem,
    sortFieldIncreasingItem,
    sortFieldDecreasingItem,
    hideFieldsItem,
    isDBAiFirstCreationEnabled,
    pinThroughColumnMenuItem,
    unpinAllColumnsMenuItem,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertFields ? SHORTCUTS.ADD_FIELD_LEFT.keyboardShortCut : undefined,
    () => handleInsertFields(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertFields
      ? SHORTCUTS.ADD_FIELD_RIGHT.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateFields ? SHORTCUTS.DUPLICATE.keyboardShortCut : undefined,
    handleDuplicateFields
  );
  // Suppress CLEAR when the selections engine is active — it owns Backspace
  // for all modes to avoid duplicate dispatches and broken undo.
  useKeyboardShortcut(
    isMenuOpen && canClearCells && !isSelectionsPerformanceEnabled
      ? SHORTCUTS.CLEAR.keyboardShortCut
      : undefined,
    handleClearFields
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteFields ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteFields
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveRight ? SHORTCUTS.MOVE_FIELDS_RIGHT.keyboardShortCut : undefined,
    () => handleMoveFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveLeft ? SHORTCUTS.MOVE_FIELDS_LEFT.keyboardShortCut : undefined,
    () => () => handleMoveFields(Position.BEFORE)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={Trigger}
      placement="bottom-start"
      isActionBarEnabled={isActionBarEnabled}
    />
  );
};

export default React.memo(SelectFieldMenu);
